/**
 * Employee sidebar modules — labels match the live sidebar menu.
 * Permission catalog comes from GET `/admins/admin-employee-permissions/`.
 */

/** Excluded from employee permission modal / HOD module picker. */
export const EXCLUDED_EMPLOYEE_PERMISSION_API_MODULES = new Set([
  "escallation",
  "reconciliation",
  "warehouse",
  "transactions",
]);

/** API `module` slug → modal stateKey (sidebar route permission key). */
export const API_MODULE_TO_STATE_KEY = {
  dashboard: "employee_sidebar.dashboard",
  seller: "crm.seller",
  orders: "employee_sidebar.orders",
  shipments: "employee_sidebar.shipments",
  remittance: "cod.remittance",
  "Team&Employee": "employee_sidebar.team_employee",
};

/** Display labels for API `module` slugs. */
export const API_MODULE_LABELS = {
  dashboard: "Dashboard",
  seller: "Seller",
  orders: "Orders",
  shipments: "Shipments",
  remittance: "COD Remittance",
  "Team&Employee": "Team & Employee",
};

/** Fallback when catalog API is unavailable. */
export const EMPLOYEE_SHIPCLUES_SIDEBAR_ITEMS = [
  {
    label: "Dashboard",
    stateKey: "employee_sidebar.dashboard",
    permissionKeyForApi: "employee_sidebar.dashboard",
    apiModule: "dashboard",
  },
  {
    label: "Seller",
    stateKey: "crm.seller",
    permissionKeyForApi: "crm.seller",
    apiModule: "seller",
  },
  {
    label: "Orders",
    stateKey: "employee_sidebar.orders",
    permissionKeyForApi: "employee_sidebar.orders",
    apiModule: "orders",
  },
  {
    label: "Shipments",
    stateKey: "employee_sidebar.shipments",
    permissionKeyForApi: "employee_sidebar.shipments",
    apiModule: "shipments",
  },
  {
    label: "COD Remittance",
    stateKey: "cod.remittance",
    permissionKeyForApi: "cod.remittance",
    apiModule: "remittance",
  },
];

/**
 * Build permission modal matrix from GET admin-employee-permissions catalog.
 * Each row: `{ id, module, code: view|create|update, name, description }`.
 */
export function buildPermissionModulesFromCatalog(catalog = []) {
  if (!Array.isArray(catalog) || catalog.length === 0) {
    return buildEmployeeShipcluesSidebarPermissionModule();
  }

  const byModule = new Map();
  catalog.forEach((p) => {
    const mod = String(p.module || "").trim();
    if (!mod) return;
    if (!byModule.has(mod)) {
      byModule.set(mod, { view: null, create: null, update: null });
    }
    const entry = byModule.get(mod);
    const code = String(p.code || "").trim().toLowerCase();
    if (code === "view" || code === "create" || code === "update") {
      entry[code] = p;
    }
  });

  const items = [...byModule.entries()]
    .filter(
      ([moduleSlug]) =>
        !EXCLUDED_EMPLOYEE_PERMISSION_API_MODULES.has(moduleSlug)
    )
    .map(([moduleSlug, group]) => {
    const stateKey =
      API_MODULE_TO_STATE_KEY[moduleSlug] ||
      `employee_sidebar.${moduleSlug}`;
    const label =
      API_MODULE_LABELS[moduleSlug] ||
      group.view?.name ||
      moduleSlug;
    return {
      label,
      stateKey,
      permissionKeyForApi: stateKey,
      apiModule: moduleSlug,
      catalogIds: {
        view: group.view?.id != null ? Number(group.view.id) : null,
        create: group.create?.id != null ? Number(group.create.id) : null,
        update: group.update?.id != null ? Number(group.update.id) : null,
      },
    };
  });

  items.sort((a, b) => a.label.localeCompare(b.label));

  return [
    {
      label: "Shipclues — employee panel",
      key: "employee_shipclues_sidebar",
      items,
    },
  ];
}

/** One accordion block: fallback static sidebar modules. */
export function buildEmployeeShipcluesSidebarPermissionModule() {
  return [
    {
      label: "Shipclues — employee panel",
      key: "employee_shipclues_sidebar",
      items: EMPLOYEE_SHIPCLUES_SIDEBAR_ITEMS.map((row) => ({
        label: row.label,
        stateKey: row.stateKey,
        permissionKeyForApi: row.permissionKeyForApi,
        apiModule: row.apiModule,
        catalogIds: { view: null, create: null, update: null },
      })),
    },
  ];
}
